/* eslint-disable no-magic-numbers, prefer-const */
/* global fetch */

import * as L from 'leaflet';
import 'whatwg-fetch';

import store from '../store/index';
import { checkStatus, parseJSON, camelize, decamelize, addQueryParams } from '../helpers/fetch-helpers';
import { getWKTBounds } from '../helpers/leaflet-helpers';

const jsonHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'application/json',
  'credentials': 'same-origin',
};

export default {
  getEmailVacancies({ brandId, email, projectId }) {
    let path = '/api/v1/email_vacancies';
    path = addQueryParams(path, { brandId, email, projectId });

    return fetch(path, {
      method: 'GET',
      headers: jsonHeaders,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  getFaces(bounds, limit, projectSlug, foundationSlug) {
    const wktBounds = getWKTBounds(bounds);
    const limitQueryString = (limit) ? `&limit=${limit}` : '';

    let scope = '';

    if (!!projectSlug) {
      scope = `project_id=${projectSlug}&`;
    } else if (!!foundationSlug) {
      scope = `foundation_id=${foundationSlug}&`;
    }

    return fetch(`/api/v1/plots?${scope}view_box=${wktBounds}${limitQueryString}`, {
      headers: jsonHeaders,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize)
      .then(response => {
        const faceResponse = [];
        response.plots.forEach((plot) => {
          if (plot.geoPoint && plot.purchasePhoto) {
            const nw = L.latLng(plot.geoPoint);
            const se = L.latLng(plot.sePoint);
            faceResponse.push(
              Object.assign(
                plot, { bounds: [nw, se] }
              )
            );
          }
        });

        return faceResponse;
      });
  },

  purchaseStart(projectId, mapPath) {
    const body = JSON.stringify(decamelize({ purchase: { projectId, mapPath } }));

    return fetch('/api/v1/purchases', {
      method: 'POST',
      headers: jsonHeaders,
      body,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  checkCoupon({ couponCode, activeProjectSlug }) {
    let checkCouponPath = `/api/v1/coupons/${couponCode}`;
    if (activeProjectSlug !== undefined) {
      checkCouponPath += `?project=${activeProjectSlug}`;
    }

    return fetch(checkCouponPath, {
      method: 'GET',
      headers: {
        'API-TOKEN': store.state.auth.apiToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'credentials': 'same-origin',
      },
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  checkEmailForCampaign({ campaignId, email }) {
    let checkEmailPath = `/api/v1/campaigns/${campaignId}/check_email`;
    checkEmailPath += `?email=${email}`;

    return fetch(checkEmailPath, {
      method: 'GET',
      headers: jsonHeaders,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  createPurchase({ projectId, name, email, uploadIdentifier, campaignId, couponId, plotSize, mapPath, pictureId }) {
    const body = JSON.stringify(decamelize({
      purchase: { projectId, name, email, uploadIdentifier, campaignId, couponId, plotSize, mapPath, pictureId } }));

    return fetch('/api/v1/purchases', {
      method: 'POST',
      headers: jsonHeaders,
      body,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  addToPurchase({ nwOffsetPoint, sideLength, purchaseId, areaId }) {
    const body = JSON.stringify(decamelize({
      plot: {
        nwOffsetPoint,
        sideLength,
        purchaseId,
        areaId,
      },
    }));

    return fetch(`/api/v1/purchases/${purchaseId}/plots`, {
      method: 'POST',
      headers: {
        'API-TOKEN': store.state.auth.apiToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'credentials': 'same-origin',
      },
      body,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  removeFromPurchase(plot, purchaseId) {
    return fetch(`/api/v1/purchases/${purchaseId}/plots/${plot.id}`, {
      method: 'DELETE',
      headers: {
        'API-TOKEN': store.state.auth.apiToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'credentials': 'same-origin',
      },
    })
      .then(checkStatus);
  },

  updatePlot(plot, purchaseId) {
    const body = JSON.stringify(decamelize(plot));

    return fetch(`/api/v1/purchases/${purchaseId}/plots/${plot.id}`, {
      method: 'PUT',
      headers: {
        'API-TOKEN': store.state.auth.apiToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'credentials': 'same-origin',
      },
      body,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  cancelPurchase(purchaseId, token) {
    return fetch(`/api/v1/purchases/${purchaseId}`, {
      method: 'DELETE',
      headers: {
        'API-TOKEN': token,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'credentials': 'same-origin',
      },
    })
      .then(checkStatus);
  },

  getPurchase(purchaseId) {
    return fetch(`/api/v1/purchases/${purchaseId}.json`, {
      method: 'GET',
      headers: Object.assign(
        { 'API-TOKEN': store.state.auth.apiToken },
        jsonHeaders
      ),
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  checkoutPurchase(purchaseId, couponCode) {
    let body = { purchase: { id: purchaseId } };

    if (couponCode) {
      body.couponCode = couponCode;
    }

    body = JSON.stringify(decamelize(body));

    return fetch(`/api/v1/purchases/${purchaseId}`, {
      method: 'PUT',
      headers: Object.assign(
        { 'API-TOKEN': store.state.auth.apiToken },
        jsonHeaders
      ),
      body,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  getProject({ projectSlug, includeAreas }) {
    let projectPath = `/api/v1/projects/${projectSlug}`;
    if (includeAreas) {
      projectPath += '?include_areas=true';
    }

    return fetch(projectPath, {
      headers: jsonHeaders,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize)
      .then(response => response.project);
  },

  getProjects({ brandId, includeAreas, projectType, foundationSlug, onlyWithActivePicAreas }) {
    let projectsPath = '/api/v1/projects';
    if (foundationSlug) {
      projectsPath = `/api/v1/foundations/${foundationSlug}/projects`;
    } else if (brandId) {
      projectsPath = `/api/v1/brands/${brandId}/projects`;
    }
    projectsPath = addQueryParams(projectsPath, { includeAreas, projectType, onlyWithActivePicAreas });

    return fetch(projectsPath, {
      headers: jsonHeaders,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize)
      .then(response => response.projects);
  },

  getProjectGroups(foundationSlug) {
    return fetch(`/api/v1/foundations/${foundationSlug}/project_groups`, {
      headers: jsonHeaders,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize)
      .then(response => response.projectGroups);
  },

  getFoundation(foundationSlug) {
    return fetch(`/api/v1/foundations/${foundationSlug}`, {
      headers: jsonHeaders,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize)
      .then(response => response.foundation);
  },

  savePicInformation(purchaseId, form, projectId) {
    const body = JSON.stringify({
      purchase: decamelize(
        Object.assign(
          form,
          { projectId }
        )
      ),
    });

    return fetch(`/api/v1/purchases/${purchaseId}`, {
      method: 'PATCH',
      headers: Object.assign(
        { 'API-TOKEN': store.state.auth.apiToken },
        jsonHeaders
      ),
      body,
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  createAlarm(alarmSectionId, date) {
    return fetch(`/api/v1/alarm_sections/${alarmSectionId}/alarms`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ alarm: { date } }),
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

  destroyAlarm(alarmId) {
    return fetch(`/api/v1/alarms/${alarmId}`, {
      method: 'DELETE',
      headers: jsonHeaders,
    })
      .then(checkStatus);
  },

  updateCheck(checkId, done) {
    return fetch(`/api/v1/checks/${checkId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify({ check: { done } }),
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(camelize);
  },

};
